import { useState } from "react";
import { Container } from "reactstrap";

import usePageTitle from "Hooks/usePageTitle";
import EditModal from "Pages/Setup/TestConnection/Components/EditModal/EditModal";
import { useCompanyConfig } from "Pages/Setup/Connect/connectShared";
import {
    BILLY_FIELDS,
    BILLY_INTEGRATIONS,
    BILLY_TESTS,
    INTACCT_FIELDS,
    INTACCT_TESTS,
} from "Pages/Setup/Connect/connectData";
import BillyConnect from "Pages/Setup/Connect/views/BillyConnect";
import IntacctConnect from "Pages/Setup/Connect/views/IntacctConnect";
import ComplianceTypeMapping from "Pages/Setup/Connect/views/ComplianceTypeMapping";
import SystemTests from "Pages/Setup/Connect/views/SystemTests";
import IntegrationDetail from "Pages/Setup/Connect/views/IntegrationDetail";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

type View =
    | "billy"
    | "billytest"
    | "intacct"
    | "intaccttest"
    | "typemap"
    | "int";

/** Credentials Calance completes off the Web Services invitation — they are not
 * part of what the client can finish, so they are excluded from the client-side
 * "required" count driving the rail. */
const CALANCE_MANAGED = ["intacct.api.login_user", "intacct.api.login_pwd"];

/** Billy + Sage Intacct twin of the Ramp + CMiC Connect page. Same shell:
 * left rail with the three sections, right panel switching views. */
export default function BillyOnboarding() {
    usePageTitle("Connect");
    const { loaded, companyId, find, onEdit, onSave, modalProps } =
        useCompanyConfigAdapter();

    const [view, setView] = useState<View>("billy");
    const [intId, setIntId] = useState<string>(BILLY_INTEGRATIONS[0].id);

    const goTo = (v: string) => {
        if (v.startsWith("int:")) {
            setIntId(v.slice(4));
            setView("int");
        } else {
            setView(v as View);
        }
    };

    const hasValue = (suffix: string) => !!find(suffix)?.keyValue?.trim();
    const asBool = (suffix: string) => {
        const value = find(suffix)?.keyValue;
        if (!value) return false;
        return ["true", "1", "yes", "y", "on"].includes(
            value.trim().toLowerCase()
        );
    };

    /* ---- connection state ---- */
    const billyRequired = BILLY_FIELDS.filter((f) => f.required);
    const billyMissing = billyRequired.filter((f) => !hasValue(f.suffix)).length;
    const billyConnected = billyMissing === 0;

    const intacctClientRequired = INTACCT_FIELDS.filter(
        (f) => f.required && !CALANCE_MANAGED.includes(f.suffix)
    );
    const intacctMissing = intacctClientRequired.filter(
        (f) => !hasValue(f.suffix)
    ).length;
    const intacctClientReady = intacctMissing === 0;
    const intacctConnected =
        intacctClientReady && CALANCE_MANAGED.every((s) => hasValue(s));

    const connectedCount = (billyConnected ? 1 : 0) + (intacctConnected ? 1 : 0);

    /* ---- compliance mapping state ---- */
    const mappingConfigured =
        hasValue("integration.vendor.compliance_type_mapping") ||
        hasValue("integration.compliance_type_mapping");

    const sysIcon = (state: "ok" | "prog" | "todo") =>
        state === "ok" ? "✓" : state === "prog" ? "!" : "°";
    const icoClass = (s: string) =>
        s === "ok" ? styles.icoOk : s === "prog" ? styles.icoProg : styles.icoTodo;

    const billyState: "ok" | "prog" | "todo" = billyConnected
        ? "ok"
        : billyMissing < billyRequired.length
        ? "prog"
        : "todo";
    const intacctState: "ok" | "prog" | "todo" = intacctConnected
        ? "ok"
        : intacctMissing < intacctClientRequired.length
        ? "prog"
        : "todo";
    const mapState: "ok" | "prog" | "todo" = mappingConfigured ? "ok" : "todo";

    /** Per-integration rail status, derived from whether its required keys are
     * set rather than the static value in the definition. */
    const integrationState = (id: string): "ok" | "prog" | "todo" => {
        const def = BILLY_INTEGRATIONS.find((i) => i.id === id);
        if (!def) return "todo";
        const required = def.fields.filter((f) => f.required);
        const set = def.fields.filter((f) => hasValue(f.suffix)).length;
        if (id === "billy_default_behavior") {
            return asBool("integration.do_attachments") ? "ok" : "todo";
        }
        if (required.length) {
            return required.every((f) => hasValue(f.suffix))
                ? "ok"
                : set > 0
                ? "prog"
                : "todo";
        }
        return set === def.fields.length ? "ok" : set > 0 ? "prog" : "todo";
    };

    /** The definition's `status` is a static placeholder. Override it with the
     * value derived from config so the detail pill agrees with the rail icon
     * instead of contradicting it. */
    const selected =
        BILLY_INTEGRATIONS.find((i) => i.id === intId) ?? BILLY_INTEGRATIONS[0];
    const integration = { ...selected, status: integrationState(selected.id) };

    return (
        <Container fluid className={styles.page}>
            <h1 className={styles.title}>Connect your systems</h1>
            <p className={styles.sub}>
                Connect Billy and Sage Intacct, map your compliance types, and
                configure integration behavior.
            </p>

            <div className={styles.wrap}>
                {/* ---------- left rail ---------- */}
                <div>
                    {/* 1 · Connect systems */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>1 · Connect systems</div>
                        <div
                            className={`${styles.sys} ${
                                view === "billy" || view === "billytest"
                                    ? styles.sysActive
                                    : ""
                            }`}
                            onClick={() => goTo("billy")}
                        >
                            <div
                                className={`${styles.ico} ${icoClass(billyState)}`}
                            >
                                {sysIcon(billyState)}
                            </div>
                            <div>
                                <div className={styles.nm}>Billy</div>
                                <div className={styles.st}>
                                    {billyConnected
                                        ? "Connected"
                                        : billyState === "prog"
                                        ? "Setup in progress"
                                        : "Not started"}
                                </div>
                            </div>
                        </div>
                        <div
                            className={`${styles.sys} ${
                                view === "intacct" || view === "intaccttest"
                                    ? styles.sysActive
                                    : ""
                            }`}
                            onClick={() => goTo("intacct")}
                        >
                            <div
                                className={`${styles.ico} ${icoClass(
                                    intacctState
                                )}`}
                            >
                                {sysIcon(intacctState)}
                            </div>
                            <div>
                                <div className={styles.nm}>Sage Intacct</div>
                                <div className={styles.st}>
                                    {intacctConnected
                                        ? "Connected"
                                        : intacctState === "prog"
                                        ? "Setup in progress"
                                        : "Not started"}
                                </div>
                            </div>
                        </div>
                        <div className={styles.progWrap}>
                            <div className={styles.bar}>
                                <i
                                    style={{
                                        width: `${(connectedCount / 2) * 100}%`,
                                    }}
                                />
                            </div>
                            <small>{connectedCount} of 2 systems connected</small>
                        </div>
                    </div>

                    {/* 2 · Compliance mapping */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>
                            2 · Compliance mapping
                        </div>
                        <div
                            className={`${styles.sys} ${styles.cmp} ${
                                view === "typemap" ? styles.sysActive : ""
                            }`}
                            onClick={() => goTo("typemap")}
                        >
                            <div className={`${styles.ico} ${icoClass(mapState)}`}>
                                {sysIcon(mapState)}
                            </div>
                            <div className={styles.nm}>Type Mapping</div>
                        </div>
                    </div>

                    {/* 3 · Integrations */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>3 · Integrations</div>
                        {BILLY_INTEGRATIONS.map((it) => {
                            const state = integrationState(it.id);
                            return (
                                <div
                                    key={it.id}
                                    className={`${styles.sys} ${styles.cmp} ${
                                        view === "int" && intId === it.id
                                            ? styles.sysActive
                                            : ""
                                    }`}
                                    onClick={() => goTo(`int:${it.id}`)}
                                >
                                    {it.id !== "billy_default_behavior" && (
                                        <div
                                            className={`${styles.ico} ${icoClass(
                                                state
                                            )}`}
                                        >
                                            {sysIcon(state)}
                                        </div>
                                    )}
                                    <div className={styles.nm}>{it.name}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ---------- right panel ---------- */}
                <div>
                    {!loaded ? (
                        <div className={styles.panel}>
                            <div className={styles.sec}>Loading…</div>
                        </div>
                    ) : view === "billy" ? (
                        <BillyConnect find={find} onEdit={onEdit} goTo={goTo} />
                    ) : view === "billytest" ? (
                        <SystemTests
                            systemName="Billy"
                            subtitle="Billy API + web session"
                            logoText="B"
                            logoClass={styles.logoBilly}
                            groups={BILLY_TESTS}
                            connectionView="billy"
                            goTo={goTo}
                            runSystem="Billy"
                        />
                    ) : view === "intacct" ? (
                        <IntacctConnect
                            find={find}
                            onEdit={onEdit}
                            goTo={goTo}
                        />
                    ) : view === "intaccttest" ? (
                        <SystemTests
                            systemName="Sage Intacct"
                            subtitle="11 read endpoints across AP, Purchasing, Projects, Company"
                            logoText="SI"
                            logoClass={styles.logoInt}
                            groups={INTACCT_TESTS}
                            connectionView="intacct"
                            goTo={goTo}
                            runSystem="Intacct"
                        />
                    ) : view === "typemap" ? (
                        <ComplianceTypeMapping
                            find={find}
                            onSave={onSave}
                            companyId={companyId}
                            goTo={goTo}
                        />
                    ) : (
                        <IntegrationDetail
                            integration={integration}
                            find={find}
                            onEdit={onEdit}
                            onSave={onSave}
                            goTo={goTo}
                        />
                    )}
                </div>
            </div>

            <EditModal {...modalProps} />
        </Container>
    );
}

/** Adapts the shared hook's `findConfig`/`editField` to the shorter names the
 * view components expect. */
function useCompanyConfigAdapter() {
    const {
        loaded,
        companyId,
        findConfig,
        editField,
        saveField,
        refreshConfigs,
        modalProps,
    } = useCompanyConfig();
    return {
        loaded,
        companyId,
        find: findConfig,
        onEdit: editField,
        onSave: saveField,
        onRefresh: refreshConfigs,
        modalProps,
    };
}
